import { useState, useRef, useEffect } from "react";
import { Thread, useThreadMessages, useSendMessage } from "@/hooks/useThreads";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Loader2, Send, Paperclip, Download,
  FileText, Image, File, X, Trash2,
} from "lucide-react";

interface Attachment { name: string; path: string; size: number; type: string }

export interface ThreadViewDraft { body: string; pendingFiles: File[] }

interface Props {
  thread: Thread;
  onBack: () => void;
  isStaff?: boolean;
  draft?: ThreadViewDraft;
  onDraftChange?: (draft: ThreadViewDraft) => void;
}

const formatBytes = (n: number) =>
  n < 1024 ? `${n}B`
  : n < 1_048_576 ? `${(n / 1024).toFixed(1)}KB`
  : `${(n / 1_048_576).toFixed(1)}MB`;

const formatTime = (d: string) =>
  new Date(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const AttachmentIcon = ({ type }: { type: string }) =>
  type.startsWith("image/") ? <Image className="h-3.5 w-3.5 shrink-0" /> :
  type === "application/pdf" ? <FileText className="h-3.5 w-3.5 shrink-0" /> :
  <File className="h-3.5 w-3.5 shrink-0" />;

const ThreadView = ({ thread, onBack, isStaff = false, draft, onDraftChange }: Props) => {
  const { user }         = useAuth();
  const { data: messages = [], isLoading, isError, refetch } = useThreadMessages(thread.id);
  const sendMessage      = useSendMessage();

  const [sendError, setSendError] = useState<string | null>(null);
  const [localBody, setLocalBody] = useState("");
  const body = draft?.body ?? localBody;
  const setBody = (value: string) => onDraftChange ? onDraftChange({ body: value, pendingFiles }) : setLocalBody(value);
  const [isInternal,   setIsInternal]   = useState(false);
  const [localFiles, setLocalFiles] = useState<File[]>([]);
  const pendingFiles = draft?.pendingFiles ?? localFiles;
  const setPendingFiles = (value: File[] | ((files: File[]) => File[])) => {
    const files = typeof value === "function" ? value(pendingFiles) : value;
    if (onDraftChange) onDraftChange({ body, pendingFiles: files });
    else setLocalFiles(files);
  };
  const [uploading,    setUploading]    = useState(false);
  const [deletingId,   setDeletingId]   = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const bottomRef    = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // "Read in portal" for staff: mark when the member sees the latest message.
  const lastMessageId = messages[messages.length - 1]?.id;
  useEffect(() => {
    if (isStaff || !lastMessageId || !user || user.id !== thread.user_id) return;
    void supabase.rpc("mark_thread_read_by_member" as never, { p_thread_id: thread.id } as never)
      .then(({ error }) => { if (error) console.warn("read mark not saved", error.message); });
  }, [isStaff, lastMessageId, user, thread.id, thread.user_id]);

  // ── Auth token helper ──────────────────────────────────────────
  const getToken = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token ?? "";
  };

  // ── Upload files to R2 via worker ─────────────────────────────
  const uploadFile = async (file: File, token: string): Promise<Attachment> => {
    const form = new FormData();
    form.append("file", file);
    form.append("type", "message");
    const res = await fetch("/api/upload", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    if (!res.ok) throw new Error(await res.text());
    const { key } = await res.json() as { key: string };
    return { name: file.name, path: key, size: file.size, type: file.type };
  };

  // ── Open/download attachment via R2 worker ────────────────────
  const openAttachment = async (att: Attachment) => {
    const token = await getToken();
    const res   = await fetch(`/api/files/${att.path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const blob    = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl; a.download = att.name; a.target = "_blank";
    a.click();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
  };

  // ── Soft-delete a message ─────────────────────────────────────
  const handleDeleteMessage = async (msgId: string, atts: Attachment[]) => {
    if (!confirm("Delete this message?")) return;
    setDeletingId(msgId);
    try {
      await supabase
        .from("thread_messages")
        .update({ deleted_at: new Date().toISOString(), body: "", attachments: [] as any })
        .eq("id", msgId);

      // Delete R2 files
      if (atts.length > 0) {
        const token = await getToken();
        await Promise.allSettled(
          atts.map((a) =>
            fetch(`/api/files/${a.path}`, {
              method: "DELETE",
              headers: { Authorization: `Bearer ${token}` },
            })
          )
        );
      }
    } finally {
      setDeletingId(null);
    }
  };

  // ── Send ──────────────────────────────────────────────────────
  const handleSend = async () => {
    if (uploading || sendMessage.isPending || (!body.trim() && pendingFiles.length === 0)) return;
    setSendError(null);
    setUploading(true);
    let uploaded: Attachment[] = [];
    try {
      if (pendingFiles.length > 0) {
        const token = await getToken();
        uploaded = await Promise.all(pendingFiles.map((f) => uploadFile(f, token)));
      }
      await sendMessage.mutateAsync({
        threadId: thread.id,
        body: body.trim(),
        isInternal: isStaff ? isInternal : false,
        attachments: uploaded,
      });
      if (onDraftChange) onDraftChange({ body: "", pendingFiles: [] });
      else { setLocalBody(""); setLocalFiles([]); }
      setIsInternal(false);
    } catch {
      setSendError("Unable to send your message. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    setPendingFiles((prev) => [...prev, ...files].slice(0, 5));
    e.target.value = "";
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-foreground truncate">{thread.subject}</h3>
          <p className="text-xs text-muted-foreground">
            Status: {thread.status.replace(/_/g, " ")} · Started {new Date(thread.created_at).toLocaleDateString()}
          </p>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {/* Message list */}
        <div className="max-h-[420px] overflow-y-auto p-4 space-y-3">
          {isError ? (
            <p role="alert" className="text-sm text-destructive">Unable to load this discussion. <Button variant="link" onClick={() => refetch()}>Retry</Button></p>
          ) : isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          ) : messages.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No messages yet</p>
          ) : (
            (messages as any[]).filter((msg) => isStaff || !msg.is_internal).map((msg: any) => {
              const isMine    = msg.sender_id === user?.id;
              const isDeleted = !!msg.deleted_at;
              const atts: Attachment[] = msg.attachments ?? [];
              // Users can delete own; staff can delete any
              const canDelete = isMine || isStaff;

              return (
                <div key={msg.id} className={`group flex ${isMine ? "justify-end" : "justify-start"}`}>
                  <div className={`relative max-w-[80%] ${isMine ? "items-end" : "items-start"} flex flex-col gap-0.5`}>
                    {/* Delete button */}
                    {!isDeleted && canDelete && (
                      <button
                        className={`absolute -top-1 ${isMine ? "-left-5" : "-right-5"} opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive`}
                        onClick={() => handleDeleteMessage(msg.id, atts)}
                        disabled={deletingId === msg.id}
                        title="Delete message"
                      >
                        {deletingId === msg.id
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <Trash2 className="h-3.5 w-3.5" />}
                      </button>
                    )}

                    {isDeleted ? (
                      <p className="text-xs italic text-muted-foreground px-3 py-2">🗑 Message deleted</p>
                    ) : (
                      <div className={`rounded-2xl px-4 py-2.5 text-sm ${
                        msg.is_internal
                          ? "bg-amber-50 border border-amber-200 text-amber-900"
                          : isMine
                            ? "bg-primary text-primary-foreground rounded-tr-sm"
                            : "bg-muted text-foreground rounded-tl-sm"
                      }`}>
                        {msg.is_internal && !isStaff && null /* hidden from non-staff */}
                        {msg.is_internal && isStaff && (
                          <p className="text-[10px] font-semibold mb-1 opacity-60">Internal note</p>
                        )}

                        {msg.body && <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{msg.body}</p>}

                        {/* Attachments */}
                        {atts.length > 0 && (
                          <div className={`space-y-1.5 ${msg.body ? "mt-2 pt-2 border-t border-current/10" : ""}`}>
                            {atts.map((att, i) => (
                              <button
                                key={i}
                                onClick={() => openAttachment(att)}
                                className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium w-full text-left transition-colors ${
                                  isMine
                                    ? "bg-white/15 hover:bg-white/25 text-primary-foreground"
                                    : "bg-background hover:bg-secondary/60 text-foreground border border-border"
                                }`}
                              >
                                <AttachmentIcon type={att.type} />
                                <span className="truncate flex-1">{att.name}</span>
                                <span className="opacity-50 shrink-0">{formatBytes(att.size)}</span>
                                <Download className="h-3 w-3 opacity-60 shrink-0" />
                              </button>
                            ))}
                          </div>
                        )}

                        <p className={`text-xs mt-1 ${isMine && !msg.is_internal ? "opacity-60" : "text-muted-foreground"}`}>
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>

        {sendError && <p role="alert" className="px-3 py-2 text-sm text-destructive">{sendError}</p>}
        {/* Reply box */}
        {thread.status !== "closed" && (
          <div className="border-t border-border p-3 space-y-2">
            {/* Staff internal toggle */}
            {isStaff && (
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={isInternal}
                  onChange={(e) => setIsInternal(e.target.checked)}
                  className="rounded"
                />
                Internal note (not visible to applicant)
              </label>
            )}

            {/* Pending files */}
            {pendingFiles.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {pendingFiles.map((f, i) => (
                  <div key={i} className="flex items-center gap-1 text-xs bg-secondary px-2 py-1 rounded-lg border border-border">
                    <AttachmentIcon type={f.type} />
                    <span className="truncate max-w-28">{f.name}</span>
                    <span className="text-muted-foreground">{formatBytes(f.size)}</span>
                    <button
                      onClick={() => setPendingFiles((p) => p.filter((_, j) => j !== i))}
                      className="text-muted-foreground hover:text-destructive ml-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2 items-end">
              {/* File picker */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                className="hidden"
                onChange={handleFileSelect}
              />
              <Button
                variant="ghost" size="sm"
                className="h-8 w-8 p-0 shrink-0 text-muted-foreground hover:text-foreground"
                onClick={() => fileInputRef.current?.click()}
                title="Attach a file"
                disabled={uploading || pendingFiles.length >= 5}
              >
                <Paperclip className="h-4 w-4" />
              </Button>

              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  pendingFiles.length > 0
                    ? "Add a message with your files… (optional)"
                    : "Type a message… (Enter to send)"
                }
                rows={2}
                className="flex-1 resize-none"
              />
              <Button
                size="sm"
                onClick={handleSend}
                aria-label="Send message"
                disabled={(!body.trim() && pendingFiles.length === 0) || sendMessage.isPending || uploading}
                className="self-end shrink-0"
              >
                {sendMessage.isPending || uploading
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Send className="h-4 w-4" />}
              </Button>
            </div>

            {pendingFiles.length > 0 && (
              <p className="text-[10px] text-muted-foreground">
                {pendingFiles.length}/5 files · max 20MB each · images, PDF, Word, Excel accepted
              </p>
            )}
          </div>
        )}

        {thread.status === "closed" && (
          <div className="border-t border-border px-4 py-3 text-sm text-muted-foreground text-center">
            This thread is closed. Contact us to reopen it.
          </div>
        )}
      </div>
    </div>
  );
};

export default ThreadView;
